import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  fetchCycleById,
  fetchCycles,
  fetchCyclesOrgWide,
  fetchCycleRuns,
  fetchRunDetail,
  fetchRunConfigCases,
  fetchRunCaseIterations,
  fetchRunCaseSteps,
  fetchScopeHistory,
  fetchCoverage,
  fetchRequirementCoverage,
  fetchReportSummary,
  fetchReportByTester,
  fetchReportByConfig,
  fetchReportRisk,
  fetchDashboard,
  fetchExecutionTrend,
  fetchScopeCut,
  fetchDashboardInsights,
  askDashboard,
  fetchCycleAiSummary,
  createReportShareToken,
  fetchReportShareTokens,
  updateReportShareToken,
  revokeReportShareToken,
  deleteReportShareToken,
  fetchPublicReport,
  fetchPublicExecution,
  verifyPublicReportPassword,
  fetchSavedReportDrafts,
  fetchSavedReportDraftById,
  createSavedReportDraft,
  updateSavedReportDraft,
  deleteSavedReportDraft,
  fetchReportEmailSchedules,
  createReportEmailSchedule,
  updateReportEmailSchedule,
  deleteReportEmailSchedule,
  type DashboardParams,
  fetchInbox,
  fetchSoloContext,
  fetchCycleDefects,
  fetchOrphanedDefects,
  fetchCyclesReferencingCase,
} from "./cycles.api";

import { useAuth } from "@/common/hooks/useAuth";

interface CycleListParams {
  workspaceVersionId?: number;
  releaseFolderId?: number;
  projectId?: number;
  status?: string;
  type?: string;
  page?: number;
  size?: number;
  query?: string;
  sort?: string;
}

export const useCycles = (params: CycleListParams, enabled = true) =>
  useQuery({
    queryKey: ["cycles", params],
    queryFn: () => fetchCycles(params),
    enabled,
  });

/** Test Manager: fetch all cycles across the whole org (no workspace version filter). */
export const useCyclesOrgWide = (
  params: Omit<CycleListParams, "workspaceVersionId"> = {},
  enabled = true,
) =>
  useQuery({
    queryKey: ["cycles-org-wide", params],
    queryFn: () => fetchCyclesOrgWide(params),
    enabled,
  });

export const useCycleById = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycle", cycleId],
    queryFn: () => fetchCycleById(cycleId!),
    enabled: !!cycleId,
  });

export const useCycleRuns = (
  cycleId: number | null,
  params?: { limit?: number },
) =>
  useQuery({
    queryKey: ["cycleRuns", cycleId, params],
    queryFn: () => fetchCycleRuns(cycleId!, params),
    enabled: !!cycleId,
    // Poll while any run is QUEUED/RUNNING so list counts stay live.
    refetchInterval: (query) => {
      const runs = query.state.data;
      const active =
        Array.isArray(runs) &&
        runs.some((r) => r.status === "RUNNING" || r.status === "QUEUED");
      return active ? 5000 : false;
    },
  });

/** Full run detail (header + per-configuration result rows). Polls while active. */
export const useCycleRunDetail = (
  cycleId: number | null,
  runId: number | null,
  opts?: { live?: boolean },
) =>
  useQuery({
    queryKey: ["cycleRunDetail", cycleId, runId],
    queryFn: () => fetchRunDetail(cycleId!, runId!),
    enabled: !!cycleId && !!runId,
    refetchInterval: opts?.live ? 5000 : false,
  });

export const useRunConfigCases = (
  cycleId: number | null,
  runId: number | null,
  configResultId: number | null,
  opts?: { live?: boolean },
) =>
  useQuery({
    queryKey: ["cycleRunConfigCases", cycleId, runId, configResultId],
    queryFn: () => fetchRunConfigCases(cycleId!, runId!, configResultId!),
    enabled: !!cycleId && !!runId && !!configResultId,
    refetchInterval: opts?.live ? 5000 : false,
  });

export const useRunCaseIterations = (
  cycleId: number | null,
  runId: number | null,
  caseResultId: number | null,
) =>
  useQuery({
    queryKey: ["cycleRunCaseIterations", cycleId, runId, caseResultId],
    queryFn: () => fetchRunCaseIterations(cycleId!, runId!, caseResultId!),
    enabled: !!cycleId && !!runId && !!caseResultId,
  });

export const useRunCaseSteps = (
  cycleId: number | null,
  runId: number | null,
  caseResultId: number | null,
  opts?: { live?: boolean },
) =>
  useQuery({
    queryKey: ["cycleRunCaseSteps", cycleId, runId, caseResultId],
    queryFn: () => fetchRunCaseSteps(cycleId!, runId!, caseResultId!),
    enabled: !!cycleId && !!runId && !!caseResultId,
    refetchInterval: opts?.live ? 5000 : false,
  });

export const useScopeHistory = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycleScopeHistory", cycleId],
    queryFn: () => fetchScopeHistory(cycleId!),
    enabled: !!cycleId,
  });

export const useCoverage = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycleCoverage", cycleId],
    queryFn: () => fetchCoverage(cycleId!),
    enabled: !!cycleId,
  });

export const useRequirementCoverage = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycleRequirementCoverage", cycleId],
    queryFn: () => fetchRequirementCoverage(cycleId!),
    enabled: !!cycleId,
  });

export const useReportSummary = (cycleId: number | null, window?: string) =>
  useQuery({
    queryKey: ["cycleReportSummary", cycleId, window],
    queryFn: () => fetchReportSummary(cycleId!, window),
    enabled: !!cycleId,
  });

export const useReportByTester = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycleReportByTester", cycleId],
    queryFn: () => fetchReportByTester(cycleId!),
    enabled: !!cycleId,
  });

export const useReportByConfig = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycleReportByConfig", cycleId],
    queryFn: () => fetchReportByConfig(cycleId!),
    enabled: !!cycleId,
  });

export const useReportRisk = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycleReportRisk", cycleId],
    queryFn: () => fetchReportRisk(cycleId!),
    enabled: !!cycleId,
  });

/**
 * Cross-cycle PM Dashboard rollup. Defaults the workspace version to the
 * logged-in user's active version so globalVersionScope authorizes it.
 */
export const useDashboard = (
  params: Omit<DashboardParams, "workspaceVersionId"> & {
    workspaceVersionId?: number;
  } = {},
  enabled = true,
) => {
  const { userPreferenceData } = useAuth();
  const versionId = userPreferenceData?.data?.versionId as number | undefined;
  // orgWide=true skips the workspace version filter (Test Manager org-wide view).
  const wv = params.orgWide
    ? undefined
    : (params.workspaceVersionId ?? versionId);
  const merged: DashboardParams = { ...params, workspaceVersionId: wv };
  return useQuery({
    queryKey: ["cycleDashboard", merged],
    queryFn: () => fetchDashboard(merged),
    enabled,
    refetchInterval: 60000,
  });
};

export const useScopeCut = (
  params: { releaseFolderId?: number; n: number; pinnedIds?: number[] },
  enabled = true,
) => {
  const { userPreferenceData } = useAuth();
  const versionId = userPreferenceData?.data?.versionId as number | undefined;
  return useQuery({
    queryKey: [
      "scopeCut",
      versionId,
      params.releaseFolderId,
      params.n,
      params.pinnedIds ?? [],
    ],
    queryFn: () =>
      fetchScopeCut({
        workspaceVersionId: versionId,
        releaseFolderId: params.releaseFolderId,
        n: params.n,
        pinnedIds: params.pinnedIds,
      }),
    enabled: enabled && params.n >= 0,
    staleTime: 10000,
  });
};

export const useInbox = (enabled = true) => {
  const { userPreferenceData } = useAuth();
  const versionId = userPreferenceData?.data?.versionId as number | undefined;
  const projectId = userPreferenceData?.data?.projectId as number | undefined;
  return useQuery({
    queryKey: ["cycleInbox", versionId, projectId],
    queryFn: () => fetchInbox({ workspaceVersionId: versionId, projectId }),
    enabled: enabled && versionId != null,
  });
};

export const useSoloContext = (enabled = true) => {
  const { userPreferenceData } = useAuth();
  const projectId = userPreferenceData?.data?.projectId as number | undefined;
  return useQuery({
    queryKey: ["cycleSoloContext", projectId],
    queryFn: () => fetchSoloContext(projectId!),
    enabled: enabled && projectId != null,
  });
};

export const useCycleDefects = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycleDefects", cycleId],
    queryFn: () => fetchCycleDefects(cycleId!),
    enabled: !!cycleId,
  });

export const useOrphanedDefects = (cycleId: number | null) =>
  useQuery({
    queryKey: ["cycleOrphanedDefects", cycleId],
    queryFn: () => fetchOrphanedDefects(cycleId!),
    enabled: !!cycleId,
  });

/** BR-50: open cycles referencing a test case (enabled only when a delete is pending). */
export const useCyclesReferencingCase = (testCaseId: number | null) =>
  useQuery({
    queryKey: ["cyclesReferencingCase", testCaseId],
    queryFn: () => fetchCyclesReferencingCase(testCaseId!),
    enabled: testCaseId != null,
    staleTime: 30_000,
  });

export const useDashboardInsights = (
  params: DashboardParams,
  enabled = true,
) => {
  const { userPreferenceData } = useAuth();
  const versionId = userPreferenceData?.data?.versionId as number | undefined;
  const merged: DashboardParams = {
    ...params,
    workspaceVersionId: params.workspaceVersionId ?? versionId,
  };
  return useQuery({
    queryKey: ["dashboardInsights", merged],
    queryFn: () => fetchDashboardInsights(merged),
    enabled: enabled && !!merged.workspaceVersionId,
    staleTime: 60_000,
  });
};

export const useAskDashboard = () =>
  useMutation({
    mutationFn: ({
      question,
      params,
    }: {
      question: string;
      params: DashboardParams;
    }) => askDashboard(question, params),
  });

export const useCycleAiSummary = () =>
  useMutation({
    mutationFn: (cycleId: number) => fetchCycleAiSummary(cycleId),
  });

export const useReportShareTokens = (
  cycleId: number | null,
  reportType?: string,
) =>
  useQuery({
    queryKey: ["reportShareTokens", cycleId, reportType],
    queryFn: () => fetchReportShareTokens(cycleId!, reportType),
    enabled: !!cycleId,
  });

export const useCreateReportShareToken = (
  cycleId: number | null,
  reportType?: string,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload?: Parameters<typeof createReportShareToken>[1]) =>
      createReportShareToken(cycleId!, payload),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["reportShareTokens", cycleId, reportType],
      }),
  });
};

export const useUpdateReportShareToken = (cycleId: number | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tokenId,
      patch,
    }: {
      tokenId: number;
      patch: Parameters<typeof updateReportShareToken>[2];
    }) => updateReportShareToken(cycleId!, tokenId, patch),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reportShareTokens", cycleId] }),
  });
};

export const useRevokeReportShareToken = (
  cycleId: number | null,
  reportType?: string,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: number) => revokeReportShareToken(cycleId!, tokenId),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["reportShareTokens", cycleId, reportType],
      }),
  });
};

export const useDeleteReportShareToken = (
  cycleId: number | null,
  reportType?: string,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: number) => deleteReportShareToken(cycleId!, tokenId),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["reportShareTokens", cycleId, reportType],
      }),
  });
};

export const useSavedReportDrafts = (scopeId?: number, reportType?: string) =>
  useQuery({
    queryKey: ["savedReportDrafts", scopeId, reportType],
    queryFn: () => fetchSavedReportDrafts(scopeId, reportType),
    staleTime: 30_000,
  });

export const useSavedReportDraft = (draftId?: number | null) =>
  useQuery({
    queryKey: ["savedReportDraft", draftId],
    queryFn: () => fetchSavedReportDraftById(draftId as number),
    enabled: draftId != null && Number.isFinite(draftId) && draftId > 0,
    staleTime: 30_000,
  });

export const useCreateSavedReportDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => createSavedReportDraft(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["savedReportDrafts"] }),
  });
};

export const useUpdateSavedReportDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      updateSavedReportDraft(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["savedReportDrafts"] });
      qc.invalidateQueries({ queryKey: ["savedReportDraft", id] });
    },
  });
};

export const useDeleteSavedReportDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteSavedReportDraft(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["savedReportDrafts"] });
      qc.removeQueries({ queryKey: ["savedReportDraft", id] });
    },
  });
};

export const usePublicReport = (token: string | null) =>
  useQuery({
    queryKey: ["publicReport", token],
    queryFn: () => fetchPublicReport(token!),
    enabled: !!token,
    staleTime: 5 * 60_000,
  });

export const usePublicExecution = (
  token: string | null,
  cycleCaseId: number | null,
) =>
  useQuery({
    queryKey: ["publicExecution", token, cycleCaseId],
    queryFn: () => fetchPublicExecution(token!, cycleCaseId!),
    enabled: !!token && !!cycleCaseId,
    staleTime: 5 * 60_000,
  });

export const useVerifyPublicReportPassword = () =>
  useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      verifyPublicReportPassword(token, password),
  });

export const useReportEmailSchedules = (cycleId: number | null) =>
  useQuery({
    queryKey: ["reportEmailSchedules", cycleId],
    queryFn: () => fetchReportEmailSchedules(cycleId!),
    enabled: !!cycleId,
  });

export const useCreateReportEmailSchedule = (cycleId: number | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof createReportEmailSchedule>[1]) =>
      createReportEmailSchedule(cycleId!, payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reportEmailSchedules", cycleId] }),
  });
};

export const useUpdateReportEmailSchedule = (cycleId: number | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scheduleId,
      payload,
    }: {
      scheduleId: number;
      payload: Parameters<typeof updateReportEmailSchedule>[2];
    }) => updateReportEmailSchedule(cycleId!, scheduleId, payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reportEmailSchedules", cycleId] }),
  });
};

export const useDeleteReportEmailSchedule = (cycleId: number | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: number) =>
      deleteReportEmailSchedule(cycleId!, scheduleId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reportEmailSchedules", cycleId] }),
  });
};

export const useExecutionTrendQuery = (
  params: DashboardParams,
  mode: "automation" | "manual" | "all" = "all",
  options?: any,
) => {
  return useQuery({
    queryKey: ["executionTrend", params, mode],
    queryFn: () => fetchExecutionTrend(params, mode),
    ...options,
  });
};
